import { type FC, useState } from "react"
import styled from "styled-components"

import dayjs from "@forento/core/utilities/dayjs"
import { toNullIfEmpty } from "@forento/core/utilities/string"
import { getBrowserTimeZone } from "@forento/core/utilities/timeZone"
import { useFile } from "@forento/ui/hooks/useFile"
import { toggleStateArray } from "@forento/ui/utilities/array"

import Button from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import Dropdown from "~/components/Dropdown"
import ExternalBuyLinkFields from "~/components/ExternalBuyLinkFields"
import ImageUploader from "~/components/ImageUploader"
import InputField, { InputTextArea, useRichTextArea } from "~/components/InputField"
import InputLabel from "~/components/InputLabel"
import {
	DefaultModal,
	ModalButtons,
	ModalContentPadding,
	ModalHeader,
	ModalHelpCenter,
	ModalHelpCenterIcon,
	ModalInputs,
	ModalText,
	ModalTitle,
} from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import useExternalBuyLink from "~/hooks/useExternalBuyLink"
import usePrice from "~/hooks/usePrice"
import { query } from "~/utilities/trpc"

type Props = { isOpen: boolean; onClose(): void; onCreated(id: number): Promise<void> }
const CreateEventModal: FC<Props> = ({ isOpen, onClose, onCreated }) => {
	const alert = useAlert()
	const platform = usePlatform()!

	const now = dayjs()

	const [title, setTitle] = useState("")
	const [shortDescription, setShortDescription] = useState("")
	const longDescription = useRichTextArea({ label: "Description" })
	const [startDate, setStartDate] = useState(now.set("minute", 0).add(1, "hour"))
	const [endDate, setEndDate] = useState(now.set("minute", 0).add(2, "hour"))
	const [frequency, setFrequency] = useState("none")
	const [repeatInterval, setRepeatInterval] = useState("1")
	const [repeatCount, setRepeatCount] = useState("4")
	const timeZone = getBrowserTimeZone()
	const recurrenceValid =
		frequency === "none" ||
		(timeZone !== null &&
			Number.isInteger(Number(repeatInterval)) &&
			Number(repeatInterval) >= 1 &&
			Number(repeatInterval) <= 12 &&
			Number.isInteger(Number(repeatCount)) &&
			Number(repeatCount) >= 2 &&
			Number(repeatCount) <= 52)
	const [isPaid, setPaid] = useState(false)
	const { price, setPrice, priceValue } = usePrice()
	const [isManualOnly, setManualOnly] = useState(false)
	const externalBuyLink = useExternalBuyLink(null)
	const [location, setLocation] = useState("")
	const [videoCallLink, setVideoCallLink] = useState("")
	const [membershipIds, setMembershipIds] = useState<number[]>([])

	const thumbnail = useFile(0, null)

	const memberships = query.membership.list.useQuery(undefined, { enabled: isOpen, refetchOnWindowFocus: false })

	const create = query.event.create.useMutation({
		async onSuccess(created) {
			setTitle("")
			setShortDescription("")
			longDescription.clear()
			thumbnail.clear()
			setStartDate(now.set("minute", 0).add(1, "hour"))
			setEndDate(now.set("minute", 0).add(2, "hour"))
			setFrequency("none")
			setRepeatInterval("1")
			setRepeatCount("4")
			setPaid(false)
			setPrice("")
			setManualOnly(false)
			externalBuyLink.reset(null)
			setLocation("")
			setVideoCallLink("")
			setMembershipIds([])

			await onCreated(created.id)
		},
		async onError(error) {
			console.error(error)
			const isScheduleError =
				error.data?.code === "BAD_REQUEST" &&
				error.data.inputErrors !== undefined &&
				Object.keys(error.data.inputErrors).length === 0
			await alert.show("Failed to create event", isScheduleError ? error.message : "Please try again later.")
		},
	})

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} width={800}>
			<ModalHeader>
				<ModalTitle>Create a new event</ModalTitle>
				<ModalHelpCenter newTab onClick="https://forento.link/create-event">
					<ModalHelpCenterIcon />
					Learn more
				</ModalHelpCenter>
			</ModalHeader>
			{memberships.error ? (
				<ModalContentPadding>Failed to load</ModalContentPadding>
			) : !memberships.data ? (
				<PartialLoadingPage />
			) : (
				<ModalInputs>
					<InputField label="Title *" value={title} onChange={setTitle} />
					<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
					{longDescription.element}
					<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
					<InputField
						inputType="datetime-local"
						label="Start date *"
						value={startDate.format("YYYY-MM-DD HH:mm")}
						onChange={value => setStartDate(dayjs(value))}
					/>
					<InputField
						inputType="datetime-local"
						label="End date *"
						value={endDate.format("YYYY-MM-DD HH:mm")}
						onChange={value => setEndDate(dayjs(value))}
					/>
					<Dropdown
						label="Repeat"
						selectedItemId={frequency}
						onChange={setFrequency}
						items={[
							{ id: "none", title: "Does not repeat" },
							{ id: "daily", title: "Daily" },
							{ id: "weekly", title: "Weekly" },
							{ id: "monthly", title: "Monthly" },
						]}
					/>
					{frequency !== "none" && (
						<div className="flex flex-col gap-3">
							<div className="grid grid-cols-2 gap-3">
								<InputField
									label={`Every (1–12 ${frequency === "daily" ? "days" : frequency === "weekly" ? "weeks" : "months"})`}
									inputType="number"
									value={repeatInterval}
									onChange={setRepeatInterval}
								/>
								<InputField
									label="Total occurrences (2–52)"
									inputType="number"
									value={repeatCount}
									onChange={setRepeatCount}
								/>
							</div>
							<p className="text-sm">
								Repeats in {timeZone ?? "your time zone"}. Each occurrence has its own attendance,
								purchases, and reminders, and can be edited or deleted individually.
								{frequency === "monthly" && " Months without this day use their last day."}
							</p>
						</div>
					)}
					<div>
						<StyledCheckbox
							label="Is paid"
							isChecked={isPaid}
							onChange={setPaid}
							isDisabled={isManualOnly}
						/>
						<InputField
							label="Price"
							value={price}
							inputType="number"
							onChange={setPrice}
							disabled={!isPaid || isManualOnly}
						/>
					</div>
					{platform.plan.access.assignProducts && (
						<StyledCheckbox label="Manual only" isChecked={isManualOnly} onChange={setManualOnly} />
					)}
					{isManualOnly && <ExternalBuyLinkFields type="event" link={externalBuyLink} />}
					<InputTextArea label="Location" value={location} onChange={setLocation} />
					<InputField label="Video call link" value={videoCallLink} onChange={setVideoCallLink} />
					{memberships.data.length > 0 && (
						<div>
							<InputLabel>Include in memberships</InputLabel>
							<Memberships>
								{memberships.data.map(membership => (
									<Checkbox
										key={membership.id}
										label={membership.title}
										isChecked={membershipIds.includes(membership.id)}
										onChange={() => toggleStateArray(setMembershipIds, membership.id)}
										isDisabled={isManualOnly}
									/>
								))}
							</Memberships>
						</div>
					)}
				</ModalInputs>
			)}
			<ModalText>
				{platform.enabledStudentEmails.includes("event-reminder")
					? "An email reminder will be sent to all enrolled members 24 hours before the event. This can be changed in email automation settings."
					: "No email reminder will be sent to enrolled members. This can be enabled in email automation settings."}
			</ModalText>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose} isDisabled={create.isPending}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={async () => {
						if (!recurrenceValid) return
						if (!startDate.isValid() || !endDate.isValid() || startDate > endDate) {
							alert.show("Invalid date", "The start date must be before the end date.")
							return
						}
						if (isManualOnly && externalBuyLink.error !== null) {
							alert.show("Error", externalBuyLink.error)
							return
						}
						create.mutate({
							title,
							shortDescription,
							longDescription: await longDescription.exportEditorState(),
							thumbnailDataUrl: await thumbnail.exportToDataUrl(),
							startDate: startDate.toDate(),
							endDate: endDate.toDate(),
							timeZone,
							recurrence:
								frequency === "daily" || frequency === "weekly" || frequency === "monthly"
									? { frequency, interval: Number(repeatInterval), count: Number(repeatCount) }
									: null,
							price: isManualOnly || !isPaid ? null : priceValue,
							isManualOnly,
							externalBuyLink: isManualOnly ? externalBuyLink.value : null,
							location: toNullIfEmpty(location.trim()) ?? null,
							videoCallLink: toNullIfEmpty(videoCallLink.trim()) ?? null,
							membershipIds: isManualOnly ? [] : membershipIds,
						})
					}}
					isLoading={create.isPending}
					isDisabled={title.trim().length === 0 || !recurrenceValid}
				>
					{frequency === "none" ? "Create event" : "Create recurring events"}
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

const StyledCheckbox = styled(Checkbox)`
	margin-bottom: 8px;
`

const Memberships = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 8px;
`

export default CreateEventModal
